import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useApp } from '../../context/AppContext';
import { downloadPDF, getPageCount } from '../../utils/pdfUtils';
import { generateFilename } from '../../utils/fileUtils';
import { PDFDocument, rgb } from 'pdf-lib';
import './PDFRedactor.css';

// Set up the worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PDFRedactor = () => {
  const { currentFile, showNotification, setCurrentView } = useApp();
  const [pdfDoc, setPdfDoc] = useState(null);
  const [selectedPage, setSelectedPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [redactions, setRedactions] = useState([]);
  const [selectedRedaction, setSelectedRedaction] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [redactionColor, setRedactionColor] = useState('#000000');
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!currentFile) return;

    const loadDocument = async () => {
      try {
        const arrayBuffer = await currentFile.file.arrayBuffer();
        const loadedPdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setPdfDoc(loadedPdf);
        setPageCount(loadedPdf.numPages);
      } catch (error) {
        console.error('Error loading PDF:', error);
      }
    };

    loadDocument();
  }, [currentFile]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(selectedPage + 1);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        const viewport = page.getViewport({ scale: zoom });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;
      } catch (error) {
        console.error('Error rendering page:', error);
      }
    };

    renderPage();
  }, [pdfDoc, selectedPage, zoom]);

  const getMousePosition = (e) => {
    const container = containerRef.current;
    if (!container) return null;

    const rect = container.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom
    };
  };

  const handleMouseDown = (e) => {
    const pos = getMousePosition(e);
    if (!pos) return;

    setIsDrawing(true);
    setDrawStart(pos);
    setCurrentRect({
      x: pos.x,
      y: pos.y,
      width: 0,
      height: 0
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDrawing || !drawStart) return;

    const pos = getMousePosition(e);
    if (!pos) return;

    setCurrentRect({
      x: Math.min(drawStart.x, pos.x),
      y: Math.min(drawStart.y, pos.y),
      width: Math.abs(pos.x - drawStart.x),
      height: Math.abs(pos.y - drawStart.y)
    });
  }, [isDrawing, drawStart, zoom]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !currentRect) return;

    if (currentRect.width > 10 && currentRect.height > 10) {
      const newRedaction = {
        id: Date.now(),
        pageIndex: selectedPage,
        rect: { ...currentRect },
        color: redactionColor
      };
      setRedactions(prev => [...prev, newRedaction]);
    }

    setIsDrawing(false);
    setDrawStart(null);
    setCurrentRect(null);
  }, [isDrawing, currentRect, selectedPage, redactionColor]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleRemoveRedaction = (id) => {
    setRedactions(prev => prev.filter(r => r.id !== id));
    if (selectedRedaction === id) {
      setSelectedRedaction(null);
    }
  };

  const handleClearPageRedactions = () => {
    setRedactions(prev => prev.filter(r => r.pageIndex !== selectedPage));
  };

  const handleSave = async () => {
    if (redactions.length === 0) {
      showNotification('Please add at least one redaction', 'warning');
      return;
    }

    setIsSaving(true);

    try {
      const pdfBytes = await currentFile.pdfDoc.save();
      const pdfDocLib = await PDFDocument.load(pdfBytes);

      for (const redaction of redactions) {
        const page = pdfDocLib.getPage(redaction.pageIndex);
        const { height: pageHeight } = page.getSize();

        // Convert hex color to RGB
        const hexToRgb = (hex) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result ? {
            r: parseInt(result[1], 16) / 255,
            g: parseInt(result[2], 16) / 255,
            b: parseInt(result[3], 16) / 255
          } : { r: 0, g: 0, b: 0 };
        };

        const color = hexToRgb(redaction.color);

        // Convert canvas Y to PDF Y
        const pdfY = pageHeight - redaction.rect.y - redaction.rect.height;

        // Draw filled rectangle for redaction
        page.drawRectangle({
          x: redaction.rect.x,
          y: pdfY,
          width: redaction.rect.width,
          height: redaction.rect.height,
          color: rgb(color.r, color.g, color.b)
        });
      }

      const filename = generateFilename(currentFile.name, 'redacted');
      await downloadPDF(pdfDocLib, filename);
      showNotification('Redacted PDF downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error saving redacted PDF:', error);
      showNotification('Failed to save redacted PDF', 'error');
    }

    setIsSaving(false);
  };

  if (!currentFile) {
    return (
      <div className="redactor-empty">
        <p>No PDF file loaded</p>
        <button className="btn btn-primary" onClick={() => setCurrentView('upload')}>
          Upload PDF
        </button>
      </div>
    );
  }

  const pageRedactions = redactions.filter(r => r.pageIndex === selectedPage);

  return (
    <div className="redactor-container">
      <div className="redactor-header">
        <button className="btn btn-ghost" onClick={() => setCurrentView('view')}>
          ← Back to Viewer
        </button>
        <h2>Redact PDF</h2>
        <div className="redactor-actions">
          <div className="color-picker">
            <label>Color:</label>
            <input
              type="color"
              value={redactionColor}
              onChange={(e) => setRedactionColor(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="redactor-content">
        <div className="redactor-sidebar">
          <h3>Pages</h3>
          <div className="page-list">
            {Array.from({ length: pageCount }, (_, i) => i).map((pageIndex) => (
              <button
                key={pageIndex}
                className={`page-item ${pageIndex === selectedPage ? 'active' : ''}`}
                onClick={() => setSelectedPage(pageIndex)}
              >
                <span>Page {pageIndex + 1}</span>
                {redactions.filter(r => r.pageIndex === pageIndex).length > 0 && (
                  <span className="redaction-badge">
                    {redactions.filter(r => r.pageIndex === pageIndex).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="redaction-instructions">
            <h3>Instructions</h3>
            <p>Click and drag on the PDF to draw redaction boxes over sensitive content.</p>
          </div>

          {pageRedactions.length > 0 && (
            <div className="redactions-list">
              <div className="redactions-header">
                <h3>Redactions on Page ({pageRedactions.length})</h3>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleClearPageRedactions}
                >
                  Clear Page
                </button>
              </div>
              {pageRedactions.map((redaction, index) => (
                <div
                  key={redaction.id}
                  className={`redaction-item ${selectedRedaction === redaction.id ? 'active' : ''}`}
                  onClick={() => setSelectedRedaction(redaction.id)}
                >
                  <div
                    className="redaction-color"
                    style={{ backgroundColor: redaction.color }}
                  />
                  <span>Redaction {index + 1}</span>
                  <button
                    className="btn btn-ghost btn-sm remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveRedaction(redaction.id);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {redactions.length > 0 && (
            <div className="total-redactions">
              Total: {redactions.length} redaction{redactions.length !== 1 ? 's' : ''} across {
                [...new Set(redactions.map(r => r.pageIndex))].length
              } page{[...new Set(redactions.map(r => r.pageIndex))].length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div className="redactor-canvas-area">
          <div className="canvas-controls">
            <div className="zoom-controls">
              <button className="btn btn-ghost" onClick={() => setZoom(prev => Math.max(prev - 0.25, 0.5))}>
                −
              </button>
              <span className="zoom-level">{Math.round(zoom * 100)}%</span>
              <button className="btn btn-ghost" onClick={() => setZoom(prev => Math.min(prev + 0.25, 3))}>
                +
              </button>
            </div>
          </div>

          <div className="canvas-wrapper">
            <div className="drawing-hint">
              Click and drag to draw redaction boxes
            </div>
            <div
              ref={containerRef}
              className="canvas-container"
              style={{ position: 'relative', display: 'inline-block' }}
              onMouseDown={handleMouseDown}
            >
              <canvas
                ref={canvasRef}
                className="pdf-canvas redacting"
              />
              {/* Render existing redactions */}
              {pageRedactions.map(redaction => (
                <div
                  key={redaction.id}
                  className={`redaction-overlay ${selectedRedaction === redaction.id ? 'selected' : ''}`}
                  style={{
                    position: 'absolute',
                    left: redaction.rect.x * zoom,
                    top: redaction.rect.y * zoom,
                    width: redaction.rect.width * zoom,
                    height: redaction.rect.height * zoom,
                    backgroundColor: redaction.color,
                    opacity: 0.8
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRedaction(redaction.id);
                  }}
                />
              ))}
              {/* Current drawing rectangle */}
              {currentRect && (
                <div
                  className="redaction-preview"
                  style={{
                    position: 'absolute',
                    left: currentRect.x * zoom,
                    top: currentRect.y * zoom,
                    width: currentRect.width * zoom,
                    height: currentRect.height * zoom,
                    backgroundColor: redactionColor,
                    opacity: 0.5
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="redactor-footer">
        <button
          className="btn btn-success"
          onClick={handleSave}
          disabled={isSaving || redactions.length === 0}
        >
          {isSaving ? (
            <>
              <span className="spinner"></span>
              Saving...
            </>
          ) : (
            `Download Redacted PDF (${redactions.length} redaction${redactions.length !== 1 ? 's' : ''})`
          )}
        </button>
      </div>
    </div>
  );
};

export default PDFRedactor;
